import type { FC } from "react";
import { useContext, useEffect } from "react";

import { ClientContext } from "~/contexts/client-context/provider";

type AnjouanGlobal = {
  init?: () => void;
};

const hasValue = (value: string | null | undefined): value is string => {
  return typeof value === "string" && value.trim().length > 0;
};

export const AnjouanLicense: FC = () => {
  const clientContext = useContext(ClientContext);
  const anjouanLicensingData = clientContext?.anjouanLicensingData;
  const { window_execute_id, element_id, script_url } =
    anjouanLicensingData ?? {};

  useEffect(() => {
    if (
      !hasValue(window_execute_id) ||
      !hasValue(element_id) ||
      !hasValue(script_url)
    ) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const el = document.getElementById(element_id);

    if (!el) {
      return;
    }

    let isDisposed = false;
    let scriptElement = document.querySelector<HTMLScriptElement>(
      `script[src="${script_url}"]`,
    );

    const initSeal = (): void => {
      if (isDisposed) {
        return;
      }

      const sealElement = document.getElementById(element_id);

      if (!sealElement) {
        return;
      }

      const globalObject = (
        window as unknown as Record<string, AnjouanGlobal | undefined>
      )[window_execute_id];

      globalObject?.init?.();
    };

    const observer = new MutationObserver(() => {
      const sealElement = document.getElementById(element_id);

      if (sealElement && sealElement.childNodes.length === 0) {
        initSeal();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    if (scriptElement) {
      scriptElement.addEventListener("load", initSeal, { once: true });
      initSeal();
    } else {
      scriptElement = document.createElement("script");
      scriptElement.async = true;
      scriptElement.src = script_url;
      scriptElement.type = "text/javascript";
      scriptElement.addEventListener("load", initSeal, { once: true });
      document.head.appendChild(scriptElement);
    }

    return () => {
      isDisposed = true;
      observer.disconnect();
      scriptElement?.removeEventListener("load", initSeal);
    };
  }, [window_execute_id, element_id, script_url]);

  return null;
};
